import { round } from '@artup/utilities'
import { Assignable } from '../common'

/**
 * An item a customer is purchasing
 */
export class OrderItem extends Assignable {
  constructor (data = {}) {
    super()
    this.assign(data)
  }

  normalize () {
    super.normalize()
    this.price = Math.max(0, this.price || 0)
    this.quantity = Math.max(0, this.quantity || 1)
    this.discount = Math.max(0, this.discount || 0)
    this.VAT = this.VAT == null
      ? undefined
      : Math.max(0, this.VAT || 0)
  }

  /**
   * Unique identifier
   * @type {String}
   */
  id

  /**
   * Identifier of the order to which the item belongs
   * @type {String}
   */
  orderId

  /**
   * Product identifier (typically an identifier of a premium service)
   * @type {String}
   */
  productId

  /**
   * Product code
   * @type {String}
   */
  code

  /**
   * Product name
   * @type {String}
   */
  name

  /**
   * Price of a single unit, exclusive VAT
   * @type {Number}
   */
  price

  /**
   * Number of sold units
   * @type {Number}
   */
  quantity

  /**
   * Discount per unit
   * @type {Number}
   */
  discount

  /**
   * Sales tax added per unit, percent.
   * If not specified, VAT is not applicable (which is different than VAT 0%)
   * @type {Number?}
   */
  VAT

  /**
   * Determines whether VAT is applicable at all (which is different than VAT 0%)
   * @type {Boolean}
   */
  get isVATApplicable () {
    return this.VAT != null
  }

  /**
   * Price per item after discounts, including VAT
   * @type {Number}
   */
  get grossPrice () {
    const { price, discount, VAT } = this
    if (price > 0) {
      return round((price - discount) * (1 + ((VAT || 0) / 100)), 2)
    } else {
      return 0
    }
  }

  /**
   * Total discount excluding VAT
   * @type {Number}
   */
  get totalDiscount () {
    const { price, quantity, discount } = this
    if (price > 0 && quantity > 0 && discount > 0) {
      return round(Math.min(price, discount) * quantity, 2)
    } else {
      return 0
    }
  }

  /**
   * Total price excluding VAT
   * @type {Number}
   */
  get netTotal () {
    const { price, quantity, totalDiscount } = this
    if (price > 0 && quantity > 0) {
      return round((price * quantity) - totalDiscount, 2)
    } else {
      return 0
    }
  }

  /**
   * Total sales tax to add
   * @type {Number}
   */
  get totalVAT () {
    const { netTotal, VAT } = this
    if (netTotal > 0) {
      return round(netTotal * (VAT / 100), 2)
    } else {
      return 0
    }
  }

  /**
   * Total including sales tax
   * @type {Number}
   */
  get total () {
    const { netTotal, totalVAT } = this
    return round(netTotal + totalVAT, 2)
  }
}
